import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import { formatDateDDMMYYEmpty } from '../../../components/common/dateUtils';

const VesselReportList = () => {
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(''); // Keep empty initially
  const [report, setReport] = useState([]);
  const printRef = useRef();

  const fetchVesselReportData = async (contractNo) => {
    if (contractNo) {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/report/vessel-report?purchase_contract_no=${contractNo}`, { withCredentials: true });
        setReport(res.data);
      } catch {
        setReport([]);
      }
    } else {
      setReport([]); // Clear report when no contract is selected
    }
  };

  useEffect(() => {
    const fetchContracts = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/report/vessel-report`, { withCredentials: true });
        setContracts(res.data);
        // REMOVED: Automatic selection of first contract
        // No longer automatically setting selectedContract
      } catch {
        setContracts([]);
      }
    };
    fetchContracts();
  }, []);

  useEffect(() => {
    fetchVesselReportData(selectedContract);
  }, [selectedContract]);

  const groupedReport = report.reduce((acc, row) => {
    if (!acc[row.order_id]) acc[row.order_id] = [];
    acc[row.order_id].push(row);
    return acc;
  }, {});

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="p-4 bg-white min-h-screen">
      <div className="max-w-full mx-auto" ref={printRef}>
        
        {/* Header Section */}
        <div className="mb-6 print:hidden">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-2xl font-bold mb-2">Vessel Report</h1>
              <p className="text-gray-600">Detailed vessel-wise import information</p>
            </div>
            <button
              onClick={handlePrint}
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              disabled={!selectedContract} // Disable print if no contract selected
            >
              Print Report
            </button>
          </div>
          
          {/* Contract Selection */}
          <div className="flex items-center gap-4 mb-4">
            <label className="font-medium">Purchase Contract No:</label>
            <div className="w-64">
              <SearchableDropdown
                options={contracts.map(c => ({ id: c.purchase_contract_no, name: c.purchase_contract_no }))}
                value={selectedContract}
                onChange={setSelectedContract}
                placeholder="Select Contract"
                displayKey="name"
                valueKey="id"
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="space-y-8">
          {!selectedContract && (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg">Please select a contract</p>
              <p className="text-sm mt-2">Choose a purchase contract number from the dropdown above to view the vessel report</p>
            </div>
          )}

          {selectedContract && report.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg">No records found</p>
              <p className="text-sm mt-2">No vessel report data available for the selected contract</p>
            </div>
          )}

          {Object.values(groupedReport).map((rows, groupIdx) => {
            const first = rows[0];
            const totalGross = rows.reduce((sum, r) => sum + (parseFloat(r.gross_weight_mt) || 0), 0);
            const orderQty = parseFloat(first.order_quantity_mt) || 0;
            const balance = orderQty - totalGross;

            return (
              <div key={first.order_id} className="border border-gray-300 rounded p-4 bg-white">
                
                {/* Vessel Information */}
                <div className="mb-6">
                  <h2 className="text-lg font-semibold mb-4 border-b pb-2">Vessel Information</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="space-y-3">
                      <div className="flex">
                        <span className="w-32 font-medium">Vessel Name:</span>
                        <span>{first.vessel_name || 'Not Available'}</span>
                      </div>
                      <div className="flex">
                        <span className="w-32 font-medium">Commodity:</span>
                        <span>{first.commodity || 'Not Available'}</span>
                      </div>
                      <div className="flex">
                        <span className="w-32 font-medium">BL Type:</span>
                        <span>{first.bl_type || 'Not Available'}</span>
                      </div>
                      <div className="flex">
                        <span className="w-32 font-medium">No. of BLs:</span>
                        <span>{first.bl_count || 'Not Available'}</span>
                      </div>
                      <div className="flex">
                        <span className="w-32 font-medium">Demurrage Amount:</span>
                        <span>
                          {first.demurrage_amount !== undefined && 
                           first.demurrage_amount !== null && 
                           first.demurrage_amount !== '' 
                            ? `${Number(first.demurrage_amount).toFixed(2)}`
                            : 'Not Available'
                          }
                        </span>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex">
                        <span className="w-36 font-medium">POL Country:</span>
                        <span>{first.pol_country || 'Not Available'}</span>
                      </div>
                      <div className="flex">
                        <span className="w-36 font-medium">Port of Discharge:</span>
                        <span>{first.port_of_discharge || 'Not Available'}</span>
                      </div>
                      <div className="flex">
                        <span className="w-36 font-medium">POD State:</span>
                        <span>{first.pod_state || 'Not Available'}</span>
                      </div>
                      <div className="flex">
                        <span className="w-36 font-medium">Departure Date:</span>
                        <span>{formatDateDDMMYYEmpty(first.departure_date) || 'Not Available'}</span>
                      </div>
                      <div className="flex">
                        <span className="w-36 font-medium">Arrival Date:</span>
                        <span>{formatDateDDMMYYEmpty(first.arrival_date) || 'Not Available'}</span>
                      </div>
                      <div className="flex">
                        <span className="w-36 font-medium">Berthing Date:</span>
                        <span>{formatDateDDMMYYEmpty(first.berthing_date) || 'Not Available'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* BL Details Table */}
                <div>
                  <h3 className="text-md font-semibold mb-3 border-b pb-2">Bill of Lading Details</h3>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300 text-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="border border-gray-300 px-3 py-2 text-left">Sr. No.</th>
                          <th className="border border-gray-300 px-3 py-2 text-left">BL Number</th>
                          <th className="border border-gray-300 px-3 py-2 text-right">Gross Weight (MT)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, idx) => (
                          <tr key={row.bl_no || idx} className="hover:bg-gray-50">
                            <td className="border border-gray-300 px-3 py-2">{idx + 1}</td>
                            <td className="border border-gray-300 px-3 py-2">{row.bl_no || 'Not Available'}</td>
                            <td className="border border-gray-300 px-3 py-2 text-right">
                              {row.gross_weight_mt ? Number(row.gross_weight_mt).toFixed(3) : 'Not Available'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Summary */}
                  <div className="mt-4 bg-gray-50 p-4 rounded border">
                    <h4 className="font-semibold mb-3">Weight Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium">Total Gross Weight:</span>
                        <span className="font-semibold">{totalGross.toFixed(3)} MT</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Shipment Quantity:</span>
                        <span className="font-semibold">{orderQty.toFixed(3)} MT</span>
                      </div>
                      <div className="flex justify-between border-t pt-2">
                        <span className="font-medium">Balance:</span>
                        <span className="font-semibold">{balance.toFixed(3)} MT</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default VesselReportList;
